import React, { CSSProperties } from "react";
import MultilineTextWithCopy from "./MultilineTextWithCopy";
import "./MultilineCellWithLeftBorder.scss";

/** Default left-border width when the cell shows a border and no width is provided. */
const DEFAULT_LEFT_BORDER_WIDTH_PX = 4;

/**
 * Value shape expected by {@link MultilineCellWithLeftBorder}.
 */
export interface MultilineCellWithLeftBorderValue {
  mainText: string;
  secondaryText?: string;
  copyValue?: string;
  shouldShowLeftBorder?: boolean;
  /** CSS color for the left accent border (e.g. `#0056D6`). */
  leftBorderColor?: string;
  /** Left border width in pixels. Defaults to 4 when the border is shown. */
  leftBorderWidth?: number;
  /** Optional CSS color for the primary (main) text line. */
  primaryTextColor?: string;
}

interface MultilineCellWithLeftBorderProps {
  value: MultilineCellWithLeftBorderValue;
}

/**
 * Clipboard text for this cell.
 * An explicit copy value wins. An `MRN : ...` label copies the identifier only.
 *
 * @param copyValue - Optional value supplied by the row
 * @param secondaryText - Secondary line shown in the cell
 * @returns Text written to the clipboard
 */
export function resolveMultilineCopyValue(
  copyValue: string | undefined,
  secondaryText: string,
): string {
  const explicit = copyValue?.trim();
  if (explicit) return explicit;
  const mrn = secondaryText.match(/^MRN\s*:\s*(.+)$/i)?.[1]?.trim();
  return mrn || secondaryText;
}

/**
 * Vertically centered multiline cell with an optional colored left border.
 * Border color, width, and primary text color come from the cell value props.
 * The copy control writes the MRN and does not activate the table row.
 *
 * @param props - Cell value with main/secondary text and border config
 * @returns Two-line cell content with optional left accent border
 */
export const MultilineCellWithLeftBorder: React.FC<
  MultilineCellWithLeftBorderProps
> = ({ value }) => {
  const mainText = value?.mainText ?? "";
  const secondaryText = value?.secondaryText ?? "";
  const shouldShowLeftBorder = Boolean(value?.shouldShowLeftBorder);
  const leftBorderColor = value?.leftBorderColor ?? "transparent";
  const leftBorderWidthPx = shouldShowLeftBorder
    ? (value?.leftBorderWidth ?? DEFAULT_LEFT_BORDER_WIDTH_PX)
    : 0;
  const copyValue = resolveMultilineCopyValue(value?.copyValue, secondaryText);
  const primaryTextColor = value?.primaryTextColor;

  const style: CSSProperties = {
    borderLeft: `${leftBorderWidthPx}px solid ${
      shouldShowLeftBorder ? leftBorderColor : "transparent"
    }`,
    ...(primaryTextColor
      ? ({ "--primary-text-color": primaryTextColor } as CSSProperties)
      : {}),
  };

  return (
    <div
      className={`table-cell-multiline-text h-full w-full justify-center py-[0.375rem]${
        primaryTextColor ? " table-cell-multiline-text--colored-primary" : ""
      }`}
      style={style}
    >
      <MultilineTextWithCopy
        primaryText={mainText}
        secondaryText={secondaryText}
        shouldTruncate
        shouldTruncateSecondary
        copyIconSize={14}
        copyValue={copyValue}
      />
    </div>
  );
};

export default MultilineCellWithLeftBorder;
